// Tour-tag editor, hoisted to the TOP of the artifact card (DESIGN §9).
// tourTags = tour membership (slugs). It's the field the docent touches on every
// review page, so it leads the card instead of being buried in the descriptive
// <ArtifactForm>. Combines the one-tap quick-reapply affordance (sticky
// last-applied + reuse chips, <TourTagQuickApply>) with the multi-tag chip editor
// + free-text datalist (<TagEditor>). CONTRACTS §3 / §10.

import type { UseArtifactDraft } from "../draft";
import { TagEditor } from "./TagEditor";
import { TourTagQuickApply } from "./TourTagQuickApply";

interface TourTagSectionProps {
  d: UseArtifactDraft;
  /** Prefix for input ids (the artifact id), so labels bind correctly. */
  idPrefix: string;
  /** Distinct tour slugs across the store, for one-tap reuse (Review pane). */
  existingTourTags?: string[];
  /** Session-sticky most-recently-applied tour slug (prominent reapply). */
  lastAppliedTour?: string | null;
  /** Notify the pane when a tour slug is applied (updates the sticky default). */
  onTourApplied?: (slug: string) => void;
}

export function TourTagSection({
  d,
  idPrefix,
  existingTourTags = [],
  lastAppliedTour = null,
  onTourApplied,
}: TourTagSectionProps) {
  const { draft, setTags } = d;

  // Apply tour slugs while keeping the sticky "last applied" value in sync: any
  // slug newly present after the change (chip tap OR free-text add) becomes the
  // new sticky default (DESIGN §9). Removals never change it.
  const setTourTags = (next: string[]) => {
    const added = next.filter((t) => !draft.tourTags.includes(t));
    setTags("tourTags", next);
    if (added.length && onTourApplied) onTourApplied(added[added.length - 1]);
  };
  const addTourTag = (slug: string) => {
    if (!slug || draft.tourTags.includes(slug)) return;
    setTourTags([...draft.tourTags, slug]);
  };

  return (
    <section className="tour-section" aria-label="Tour tags">
      <div className="tour-section__head">
        <label className="tour-section__label" htmlFor={`${idPrefix}-tourTags`}>
          Tour tags
        </label>
        <span className="tour-section__hint">tour membership</span>
      </div>
      <TourTagQuickApply
        current={draft.tourTags}
        existing={existingTourTags}
        lastApplied={lastAppliedTour}
        onApply={addTourTag}
      />
      <TagEditor
        inputId={`${idPrefix}-tourTags`}
        values={draft.tourTags}
        slug
        placeholder="tour slug"
        suggestions={existingTourTags}
        onChange={setTourTags}
      />
    </section>
  );
}
